import React, { useEffect, useState } from 'react';

import { Bell, Dot, Search } from 'lucide-react';
import Image from '@/components/ui/image';
import Link from '@/components/ui/link';
import { useLocation } from 'react-router';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const mockNotifications: Array<{
  id: number;
  type: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
}> = [
  {
    id: 1,
    type: 'airing',
    title: 'Notifications is coming soon',
    message: 'Notifications are not available for now, Check back later.',
    time: 'Announcement',
    read: true,
  },
];

type NotificationItem = {
  id: number;
  type: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
};

function getRouteLabel(route: string): string {
  if (route === '/') return 'Home';
  if (route.startsWith('/discover')) return 'Discover';
  if (route.startsWith('/search')) return 'Search';
  if (route.startsWith('/now')) return 'Now';
  if (route.startsWith('/music')) return 'Music';
  if (route.startsWith('/users')) return 'Users';
  if (route.startsWith('/u/') || route.startsWith('/@')) return 'Profile';
  if (route.startsWith('/changelogs')) return 'Changelogs';
  if (route === '/settings') return 'Settings';
  return '1Anime';
}

function shouldHideMobileTopNav(pathname: string | null): boolean {
  return (
    !pathname ||
    pathname.startsWith('/anime/') ||
    pathname.startsWith('/manga/')
  );
}

function useNotificationsDismiss(
  setShowNotifications: React.Dispatch<React.SetStateAction<boolean>>
): void {
  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      if (
        !target.closest('[data-notifications-dropdown]') &&
        !target.closest('[data-notifications-button]')
      ) {
        setShowNotifications(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowNotifications]);
}

function useDiscoverGlassShell(isDiscoverPage: boolean): boolean {
  const [showDiscoverGlassShell, setShowDiscoverGlassShell] = useState(false);

  useEffect(() => {
    if (!isDiscoverPage) {
      setShowDiscoverGlassShell(false);
      return () => {};
    }

    const updateDiscoverShellState = () => {
      const heroSection = document.querySelector(
        '[data-discover-hero]'
      ) as HTMLElement | null;

      if (!heroSection) {
        setShowDiscoverGlassShell(window.scrollY > 120);
        return;
      }

      const threshold = Math.max(120, heroSection.offsetHeight - 110);
      setShowDiscoverGlassShell(window.scrollY > threshold);
    };

    updateDiscoverShellState();
    window.addEventListener('scroll', updateDiscoverShellState, {
      passive: true,
    });
    window.addEventListener('resize', updateDiscoverShellState);

    return () => {
      window.removeEventListener('scroll', updateDiscoverShellState);
      window.removeEventListener('resize', updateDiscoverShellState);
    };
  }, [isDiscoverPage]);

  return !isDiscoverPage || showDiscoverGlassShell;
}

function TopBarActions({
  useGlassShell,
  unreadCount,
  onToggleNotifications,
}: {
  useGlassShell: boolean;
  unreadCount: number;
  onToggleNotifications: () => void;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-1.5">
      <Link
        href="/search"
        aria-label="Open search"
        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          useGlassShell
            ? 'border border-border/50 bg-transparent text-muted-foreground hover:text-foreground'
            : 'border border-white/30 bg-black/25 text-white backdrop-blur-sm hover:bg-black/35'
        }`}
      >
        <Search className="h-4 w-4" />
      </Link>
      <Button
        variant="ghost"
        size="icon"
        className={`relative h-8 w-8 rounded-lg hover:bg-transparent ${
          useGlassShell
            ? 'border border-border/50 bg-transparent text-muted-foreground hover:text-foreground'
            : 'border border-white/30 bg-black/25 text-white backdrop-blur-sm hover:bg-black/35 hover:text-white'
        }`}
        data-notifications-button
        onClick={onToggleNotifications}
        aria-label="Open notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 ? (
          <Badge className="absolute -right-1 -top-1 h-4 min-w-4 rounded-full px-1 text-[10px]">
            {unreadCount}
          </Badge>
        ) : (
          <Dot className="absolute -right-2 -top-2 h-4 w-4 text-muted-foreground/60" />
        )}
      </Button>
    </div>
  );
}

function NotificationsDropdown({
  notifications,
}: {
  notifications: NotificationItem[];
}): React.ReactElement {
  return (
    <div
      className="fixed right-2 z-50 w-[min(22rem,calc(100vw-1rem))] overflow-hidden rounded-2xl border border-border/60 bg-card/75 shadow-lg backdrop-blur-xl md:hidden"
      data-notifications-dropdown
      style={{
        top: `calc(3.5rem + env(safe-area-inset-top))`,
      }}
    >
      <div className="border-b border-border/60 bg-muted/25 p-3 backdrop-blur-sm">
        <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`border-b border-border/70 p-3 transition-colors last:border-b-0 hover:bg-muted/50${
              notification.read ? '' : 'bg-primary/5'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-medium text-foreground">
                  {notification.title}
                </h4>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {notification.message}
                </p>
                <span className="mt-1 inline-block text-xs text-muted-foreground">
                  {notification.time}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MobileTopNav(): React.ReactElement | null {
  const pathname = useLocation().pathname;
  const [showNotifications, setShowNotifications] = useState(false);
  const unreadCount = mockNotifications.filter((n) => !n.read).length;
  const isDiscoverPage = pathname?.startsWith('/discover') ?? false;
  const useGlassShell = useDiscoverGlassShell(isDiscoverPage);

  useNotificationsDismiss(setShowNotifications);

  if (shouldHideMobileTopNav(pathname)) return null;

  return (
    <>
      <header
        className={`fixed left-0 right-0 top-0 z-40 md:hidden ${
          useGlassShell ? 'bg-transparent' : 'bg-transparent'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          height: 'calc(3.5rem + env(safe-area-inset-top))',
        }}
      >
        <div
          className={`mx-3 my-1.5 flex h-[calc(3.5rem-0.75rem)] items-center justify-between rounded-2xl px-2.5 transition-all ${
            useGlassShell
              ? 'border border-border/55 bg-background/45 shadow-lg backdrop-blur-xl'
              : 'border-none bg-background/20 shadow-none backdrop-blur-[2px]'
          }`}
        >
          <Link href="/" className="flex min-w-0 items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                useGlassShell
                  ? 'border border-border/50 bg-transparent'
                  : 'border border-white/30 bg-black/25 backdrop-blur-sm'
              }`}
            >
              <Image
                src="/logo.svg"
                alt="1Anime Logo"
                width={24}
                height={24}
                className="h-5.5 w-5.5"
              />
            </div>
            <p
              className={`truncate text-xs font-medium ${
                useGlassShell ? 'text-foreground' : 'text-white drop-shadow'
              }`}
            >
              {getRouteLabel(pathname)}
            </p>
          </Link>

          <TopBarActions
            useGlassShell={useGlassShell}
            unreadCount={unreadCount}
            onToggleNotifications={() =>
              setShowNotifications((value) => !value)
            }
          />
        </div>
      </header>
      {showNotifications && (
        <NotificationsDropdown notifications={mockNotifications} />
      )}
    </>
  );
}
